import { useEffect, useState } from "react";
import { Camera, Mic, PackageCheck, Send, Trash2, Type, X } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import VoiceRecorder from "./VoiceRecorder";
import PlayerNutritionSummary from "./PlayerNutritionSummary";
import { audioFilename, voiceUploadError } from "../lib/audio";

const modes = [
  ["stock", PackageCheck, "fromMyStock"],
  ["text", Type, "describeMeal"],
  ["photo", Camera, "choosePhoto"],
  ["voice", Mic, "voice"],
];

export default function MealReplacementSheet({ item, future = false, onClose, onSaved }) {
  const { t, lang } = useI18n();
  const [mode, setMode] = useState("stock");
  const [stock, setStock] = useState([]);
  const [stockState, setStockState] = useState("loading");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [voice, setVoice] = useState(null);
  const [voiceUrl, setVoiceUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const recommended = stock.filter((row) => Number(row.suitability_score) >= 80);
  const further = stock.filter((row) => Number(row.suitability_score) < 80);

  useEffect(() => {
    let live = true;
    api.get(`/me/schedule-items/${item.id}/meal-alternatives/stock`)
      .then(({ data }) => { if (live) { setStock(data.alternatives || []); setStockState("ready"); } })
      .catch(() => { if (live) setStockState("error"); });
    return () => { live = false; };
  }, [item.id]);

  useEffect(() => () => {
    if (photoUrl) URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);
  useEffect(() => () => {
    if (voiceUrl) URL.revokeObjectURL(voiceUrl);
  }, [voiceUrl]);

  const complete = (data) => {
    onSaved(data.today_item);
    onClose();
  };
  const selectStock = async (inventoryItemId) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/me/schedule-items/${item.id}/meal-alternatives/stock`, {
        inventory_item_id: inventoryItemId,
      });
      complete(data);
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendText = async () => {
    if (!description.trim()) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/me/schedule-items/${item.id}/meal-alternatives/text`, {
        description: description.trim(),
      });
      complete(data);
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const choosePhoto = (file) => {
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhoto(file || null);
    setPhotoUrl(file ? URL.createObjectURL(file) : null);
  };
  const captureVoice = async (blob, duration, type) => {
    if (voiceUrl) URL.revokeObjectURL(voiceUrl);
    setVoice({ blob, duration, type });
    setVoiceUrl(URL.createObjectURL(blob));
  };
  const removeVoice = () => {
    if (voiceUrl) URL.revokeObjectURL(voiceUrl);
    setVoice(null); setVoiceUrl(null);
  };
  const sendPhoto = async () => {
    if (!photo) return;
    setBusy(true); setError("");
    const form = new FormData();
    form.append("photo", photo);
    if (description.trim()) form.append("description", description.trim());
    if (voice) {
      form.append("voice", voice.blob, audioFilename("meal-note", voice.type));
      form.append("voice_duration", String(voice.duration));
    }
    try {
      const { data } = await api.post(`/me/schedule-items/${item.id}/meal-alternatives/photo`, form);
      complete(data);
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendVoice = async (blob, duration, type) => {
    setBusy(true); setError("");
    const form = new FormData();
    form.append("audio", blob, audioFilename("meal-note", type));
    try {
      const { data } = await api.post(`/me/schedule-items/${item.id}/meal-alternatives/audio`, form);
      complete(data);
    } catch (err) {
      setError(voiceUploadError(err, t, apiError(err.response?.data?.detail)));
      throw err;
    } finally { setBusy(false); }
  };

  return (
    <div lang={lang} className="pc-player-ui fixed inset-0 flex items-end justify-center" style={{ zIndex: 70, background: "rgba(0,0,0,.76)" }} data-testid="meal-replacement-sheet">
      <section className="pc-player-meal-sheet w-full max-w-xl rounded-t-[28px] p-5 max-h-[91vh] overflow-y-auto pc-scroll" style={{ background: "var(--pc-charcoal)", border: "1px solid var(--pc-border)" }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs break-words" style={{ color: "var(--pc-muted)" }}>{item.title}</p>
            <h2 className="text-xl font-display font-semibold mt-1">{t(future ? "mealExchangeTitle" : "mealReplacementTitle")}</h2>
          </div>
          <button type="button" onClick={onClose} className="pc-btn pc-btn-ghost pc-player-close" aria-label={t("close")}><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-5" data-testid="meal-replacement-modes">
          {modes.map(([key, Icon, label]) => (
            <button key={key} type="button" onClick={() => { setMode(key); setError(""); }} className="pc-player-meal-mode rounded-xl p-3 text-left text-xs flex items-center gap-2" style={{ background: mode === key ? "var(--pc-cyan-dim)" : "var(--pc-charcoal-2)", color: mode === key ? "var(--pc-cyan)" : "var(--pc-text)", border: `1px solid ${mode === key ? "var(--pc-cyan)" : "var(--pc-border)"}` }}>
              <Icon size={16} className="shrink-0" /> <span className="min-w-0">{t(label)}</span>
            </button>
          ))}
        </div>

        {mode === "stock" && <div className="space-y-2 mt-5" data-testid="prepared-meal-options">
          {stockState === "loading" && <div className="flex justify-center py-8"><div className="pc-spinner" /></div>}
          {stockState !== "loading" && stock.length === 0 && <p className="text-sm py-4" style={{ color: "var(--pc-muted)" }}>{t("noPreparedMeals")}</p>}
          {recommended.length > 0 && <p className="text-[10px] uppercase tracking-[.13em]" style={{ color: "var(--pc-cyan)" }}>{t("recommended")}</p>}
          {recommended.map((row) => <button type="button" disabled={busy} onClick={() => selectStock(row.inventory_item_id)} key={row.inventory_item_id} className="pc-card p-4 w-full text-left" data-testid={`prepared-meal-${row.inventory_item_id}`}>
            <div className="flex justify-between gap-3"><p className="font-semibold">{row.meal.name}</p><span className="text-xs" style={{ color: "var(--pc-cyan)" }}>{Math.round(row.suitability_score)}%</span></div>
            <p className="text-xs mt-2" style={{ color: "var(--pc-muted)" }}>{row.quantity} × · <PlayerNutritionSummary nutrition={row.meal.nutrition} compact /></p>
          </button>)}
          {further.length > 0 && <p className="text-[10px] uppercase tracking-[.13em] pt-2" style={{ color: "var(--pc-muted)" }}>{t("furtherAlternatives")}</p>}
          {further.map((row) => <button type="button" disabled={busy} onClick={() => selectStock(row.inventory_item_id)} key={row.inventory_item_id} className="pc-card p-4 w-full text-left" data-testid={`prepared-meal-${row.inventory_item_id}`}><div className="flex justify-between gap-3"><p className="font-semibold">{row.meal.name}</p><span className="text-xs" style={{ color: "var(--pc-score-medium)" }}>{Math.round(row.suitability_score)}%</span></div><p className="text-xs mt-2" style={{ color: "var(--pc-muted)" }}>{row.quantity} × · <PlayerNutritionSummary nutrition={row.meal.nutrition} compact /></p></button>)}
        </div>}

        {mode === "text" && <div className="mt-5">
          <label className="text-sm font-medium" htmlFor="replacement-description">{t("describeMeal")}</label>
          <textarea id="replacement-description" className="pc-input mt-2" rows="5" value={description} onChange={(event) => setDescription(event.target.value)} />
          <button type="button" disabled={busy || !description.trim()} onClick={sendText} className="pc-btn pc-btn-primary w-full mt-3 flex items-center justify-center gap-2"><Send size={16} /> {t(future ? "saveMealExchange" : "sendReplacementMeal")}</button>
        </div>}

        {mode === "photo" && <div className="mt-5">
          {!photo && <label className="pc-card p-6 flex flex-col items-center gap-3 cursor-pointer" data-testid="meal-photo-picker"><Camera size={28} color="var(--pc-cyan)" /><span className="text-sm font-semibold">{t("choosePhoto")}</span><input type="file" accept="image/*" capture="environment" hidden onChange={(event) => choosePhoto(event.target.files?.[0])} /></label>}
          {photo && <div data-testid="meal-photo-preview">
            <img src={photoUrl} alt="" className="w-full max-h-64 object-cover rounded-2xl" />
            <button type="button" onClick={() => choosePhoto(null)} className="pc-btn pc-btn-ghost mt-2 flex items-center gap-2"><Trash2 size={15} /> {t("removePhoto")}</button>
            <label className="text-sm block mt-4" htmlFor="photo-description">{t("optionalDescription")}</label>
            <textarea id="photo-description" className="pc-input mt-2" rows="3" value={description} onChange={(event) => setDescription(event.target.value)} />
            <div className="mt-4">
              <p className="text-sm mb-2">{t("addVoice")}</p>
              {!voice && <VoiceRecorder onSend={captureVoice} sendLabel={t("addVoice")} />}
              {voice && <div className="rounded-xl p-3" style={{ background: "var(--pc-charcoal-2)" }} data-testid="meal-photo-voice-preview"><p className="text-xs mb-2" style={{ color: "var(--pc-muted)" }}>{t("voiceAdded")}</p><audio controls src={voiceUrl} className="w-full" /><button type="button" className="pc-btn pc-btn-ghost mt-2" onClick={removeVoice}>{t("removeVoice")}</button></div>}
            </div>
            <button type="button" disabled={busy} onClick={sendPhoto} className="pc-btn pc-btn-primary w-full mt-5 flex items-center justify-center gap-2" data-testid="send-photo-replacement"><Send size={16} /> {t(future ? "saveMealExchange" : "sendReplacementMeal")}</button>
          </div>}
        </div>}

        {mode === "voice" && <div className="mt-5"><VoiceRecorder disabled={busy} onSend={sendVoice} sendLabel={t(future ? "saveMealExchange" : "sendReplacementMeal")} /></div>}
        {error && <p className="text-sm mt-4" style={{ color: "var(--pc-score-poor)" }}>{error}</p>}
      </section>
    </div>
  );
}
